import { Link } from "react-router-dom";
import { Heart, Star } from "lucide-react";
import { useWishlistActions } from "../hooks/useWishlistActions";
import { resolveProductImage } from "../utils/productAssets";
import { formatCount } from "../utils/formatCount";
import { getDisplaySoldCount } from "../utils/displaySoldCount";

export function StoreStars({ count = 4 }) {
  const safeCount = Math.max(0, Math.min(5, Math.round(count || 0)));
  return (
    <div className="flex items-center gap-0.5 text-amber-500">
      {Array.from({ length: 5 }).map((_, index) => (
        <Star
          className={index < safeCount ? "h-3.5 w-3.5 fill-current" : "h-3.5 w-3.5 text-slate-300"}
          key={index}
        />
      ))}
    </div>
  );
}

export default function StoreProductCard({ product, compact = false }) {
  const { isSaved, loading, toggleWishlist } = useWishlistActions(product);
  const soldCount = getDisplaySoldCount(product);
  const reviewCount = Number(product.reviewCount || 0);
  const rating = Number(product.averageRating || product.rating || 0);

  return (
    <Link
      className="group block overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition duration-200 hover:-translate-y-1 hover:border-[#cfe6fa] hover:shadow-[0_20px_45px_rgba(15,23,42,0.10)]"
      to={`/products/${product.slug || product.sku}`}
    >
      <div className={`relative flex items-center justify-center overflow-hidden bg-gradient-to-b from-white to-slate-50 ${compact ? "h-40" : "h-52"} p-3`}>
        <span className="absolute left-3 top-3 z-10 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-[#3991D5] shadow-sm">
          {product.brandLine || "Product"}
        </span>
        <button
          aria-label={isSaved ? "Remove from wishlist" : "Add to wishlist"}
          className={`absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border bg-white/95 shadow-sm transition ${isSaved ? "border-red-100 text-red-500" : "border-slate-100 text-slate-400 hover:text-red-500"}`}
          disabled={loading}
          onClick={toggleWishlist}
          type="button"
        >
          <Heart className="h-4 w-4" fill={isSaved ? "currentColor" : "none"} />
        </button>
        <img
          alt={product.name}
          className="max-h-full max-w-full object-contain transition duration-300 group-hover:scale-[1.03]"
          src={resolveProductImage(product.image)}
        />
      </div>
      <div className="space-y-1 p-3">
        <h3 className="line-clamp-2 min-h-[2.75rem] text-left font-gopher text-[15px] font-bold leading-5 text-slate-900">
          {product.name}
        </h3>
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <StoreStars count={rating || 4} />
            <p className="text-[11px] font-semibold text-slate-400 mb-1">
              {reviewCount ? `${formatCount(reviewCount)} review${reviewCount === 1 ? "" : "s"}` : "No reviews"}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
          <span className="inline-flex items-center rounded-full bg-[#e0f4ff] px-3 py-1 text-md font-bold text-[#3991D5]">
            ${Number(product.price || 0).toFixed(2)}
          </span>
          {product.compareAtPrice && (
            <span className="text-xs font-semibold text-slate-400 line-through">
              ${Number(product.compareAtPrice).toFixed(2)}
            </span>
          )}
          <div className="text-right flex gap-1 items-center">
            <p className="mb-0 text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">Sold</p>
            <p className="mb-0 text-sm font-black text-slate-700">{formatCount(soldCount)}</p>
          </div>
        </div>
      </div>
    </Link>
  );
}
